import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@gettactic/helpers";

export const badgeVariants = cva(
  "inline-flex items-center rounded-md border text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline:
          "text-foreground border-neutral-100 text-[12px] font-[500] text-neutral-800 whitespace-nowrap"
      },
      size: {
        sm: "px-2 py-0.5 text-xs",
        xsm: "px-1 py-0.5 text-[13px]",
        md: "px-2.5 py-0.5 text-sm",
        lg: "px-3 py-1 text-sm"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "md"
    }
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

/**
 * A short label such as a status, a count or a tag. `variant`: primary (`default`),
 * `secondary`, `destructive` or `outline`; `size` from `xsm` to `lg`. For request and
 * reservation statuses use `StatusBadge`.
 */
export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(badgeVariants({ variant, size }), className)}
        {...props}
      />
    );
  }
);
